import { z } from 'zod';
import { ALL_PAYMENT_MODES, PaymentMode, PaymentProvider } from './enums';

/**
 * Os "slots" de credencial de cada PSP × modo que o admin edita em `/psp` —
 * nome do secret no Secret Manager, prefixo esperado e se dá para testar
 * conexão.
 *
 * Origem: #756 — 2026-10-01 13:31 +0200 (owner: "todo setup PSP deve ficar em
 * /psp e deve poder também cadastrar sandbox de cada PSP", e a chave é digitada
 * na tela).
 *
 * Por quê: as chaves eram `defineSecret`, fixadas no deploy; trocar exigia
 * `gcloud` + redeploy. Agora o callable `adminSetPspSecret` grava uma versão
 * nova no MESMO secret, e as Functions leem `latest` em tempo de execução
 * (`apps/functions/src/lib/pspSecretStore.ts`). Os nomes NÃO mudam — as
 * declarações `secrets: [...]` continuam valendo como fallback e como IAM.
 *
 * Contrato: lista fechada. O valor da chave NUNCA passa pelo Firestore (o
 * `/config/psps` é legível por qualquer usuário logado) nem volta para a tela —
 * só os 4 últimos caracteres.
 *
 * Usado por: Functions — `lib/pspSecretStore.ts`, `admin/pspSecrets.ts`;
 * admin — `features/psp/PspEditorPage.tsx`.
 *
 * Dados: o webhook do Stripe editável é o do endpoint ATUAL
 * (`stripeWebhookDahlia20260527`), um segredo por modo. Os endpoints legados
 * (`STRIPE_WEBHOOK_SECRET`, `_DAHLIA_20260422`) ficam fora da tela.
 *
 * Dicas: o prefixo é o mesmo que o `AsaasProviderClient` confere no construtor
 * (`$aact_prod_`/`$aact_hmlg_`) — chave de um ambiente no slot do outro é
 * recusada antes de gravar.
 */

export const PspSecretKind = {
  API_KEY: 'api_key',
  WEBHOOK: 'webhook',
  WITHDRAWAL: 'withdrawal',
} as const;
export type PspSecretKind = (typeof PspSecretKind)[keyof typeof PspSecretKind];
export const ALL_PSP_SECRET_KINDS = Object.values(PspSecretKind) as [PspSecretKind, ...PspSecretKind[]];

/** Um slot de credencial. */
export type PspSecretSlot = {
  readonly provider: string;
  readonly mode: PaymentMode;
  readonly kind: PspSecretKind;
  /** Nome do secret no Secret Manager (= o `defineSecret`). */
  readonly secretName: string;
  /** Prefixos aceitos; vazio = qualquer valor (token gerado por nós). */
  readonly prefixes: readonly string[];
  /** Existe teste de conexão online para este slot (só chave de API). */
  readonly testable: boolean;
};

export const PSP_SECRET_SLOTS: readonly PspSecretSlot[] = [
  { provider: PaymentProvider.STRIPE, mode: PaymentMode.LIVE, kind: PspSecretKind.API_KEY, secretName: 'STRIPE_SECRET_KEY', prefixes: ['sk_live_', 'rk_live_'], testable: true },
  { provider: PaymentProvider.STRIPE, mode: PaymentMode.SANDBOX, kind: PspSecretKind.API_KEY, secretName: 'STRIPE_SECRET_KEY_SANDBOX', prefixes: ['sk_test_', 'rk_test_'], testable: true },
  { provider: PaymentProvider.STRIPE, mode: PaymentMode.LIVE, kind: PspSecretKind.WEBHOOK, secretName: 'STRIPE_WEBHOOK_SECRET_DAHLIA_20260527_LIVE', prefixes: ['whsec_'], testable: false },
  { provider: PaymentProvider.STRIPE, mode: PaymentMode.SANDBOX, kind: PspSecretKind.WEBHOOK, secretName: 'STRIPE_WEBHOOK_SECRET_DAHLIA_20260527', prefixes: ['whsec_'], testable: false },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.LIVE, kind: PspSecretKind.API_KEY, secretName: 'ASAAS_API_KEY', prefixes: ['$aact_prod_'], testable: true },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.SANDBOX, kind: PspSecretKind.API_KEY, secretName: 'ASAAS_API_KEY_SANDBOX', prefixes: ['$aact_hmlg_'], testable: true },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.LIVE, kind: PspSecretKind.WEBHOOK, secretName: 'ASAAS_WEBHOOK_TOKEN', prefixes: [], testable: false },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.SANDBOX, kind: PspSecretKind.WEBHOOK, secretName: 'ASAAS_WEBHOOK_TOKEN_SANDBOX', prefixes: [], testable: false },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.LIVE, kind: PspSecretKind.WITHDRAWAL, secretName: 'ASAAS_WITHDRAWAL_TOKEN', prefixes: [], testable: false },
  { provider: PaymentProvider.ASAAS, mode: PaymentMode.SANDBOX, kind: PspSecretKind.WITHDRAWAL, secretName: 'ASAAS_WITHDRAWAL_TOKEN_SANDBOX', prefixes: [], testable: false },
];

/** O slot de um PSP × modo × tipo, ou `null`. */
export function pspSecretSlot(provider: string, mode: PaymentMode, kind: PspSecretKind): PspSecretSlot | null {
  return PSP_SECRET_SLOTS.find((s) => s.provider === provider && s.mode === mode && s.kind === kind) ?? null;
}

/**
 * O valor cabe no slot? Prefixo do ambiente certo, sem espaço, tamanho sano.
 * Puro — a tela usa para bloquear antes de enviar; o callable revalida.
 */
export type PspSecretValueProblem = 'empty' | 'whitespace' | 'too_short' | 'too_long' | 'wrong_prefix';

export function pspSecretValueProblem(slot: PspSecretSlot, value: string): PspSecretValueProblem | null {
  if (value.length === 0) return 'empty';
  // Só ASCII visível. Auditoria 2026-10-01 (#756, M1): `\s` não pegava
  // caractere de controle; com um NUL na chave o `fetch` do teste de conexão
  // lançava com o VALOR inteiro na mensagem, que ia para log e resposta.
  if (!/^[\x21-\x7E]+$/.test(value)) return 'whitespace';
  if (value.length > 4096) return 'too_long';
  if (slot.prefixes.length > 0 && !slot.prefixes.some((p) => value.startsWith(p))) return 'wrong_prefix';
  // Token gerado por nós (webhook/saque do Asaas): curto demais é adivinhável.
  if (slot.prefixes.length === 0 && value.length < 16) return 'too_short';
  return null;
}

/** Input do callable `adminSetPspSecret`. */
export const SetPspSecretInputSchema = z.object({
  provider: z.string().min(1).max(40),
  mode: z.enum(ALL_PAYMENT_MODES),
  kind: z.enum(ALL_PSP_SECRET_KINDS),
  value: z.string().min(1).max(4096),
  reason: z.string().trim().min(1).max(500),
});
export type SetPspSecretInput = z.infer<typeof SetPspSecretInputSchema>;

/** Input do callable `adminTestPspConnection`. */
export const TestPspConnectionInputSchema = z.object({
  provider: z.string().min(1).max(40),
  mode: z.enum(ALL_PAYMENT_MODES),
});
export type TestPspConnectionInput = z.infer<typeof TestPspConnectionInputSchema>;

/** Status de um slot, como o admin vê (nunca o valor). */
export type PspSecretStatus = {
  readonly provider: string;
  readonly mode: PaymentMode;
  readonly kind: PspSecretKind;
  readonly configured: boolean;
  readonly version: string | null;
  readonly createdAt: number | null;
  readonly last4: string | null;
};
